import type { FetchFormResponse } from '../../service/types';
import gdadSkillsMappingJson from '../../config/pages/gdad/data/skills-mapping.json';
import { getGdadSkillScore } from '../../utils/gdad-proficiency';

type GdadSkill = {
  id: string;
  name: string;
};

type GdadRoleSkill = {
  skill_id: string;
  order: number;
};

type GdadSkillsMapping = {
  skills: GdadSkill[];
  role_level_skills_map: Record<string, GdadRoleSkill[]>;
};

type SkillScoreRow = {
  skillName: string;
  score: number;
};

type Props = {
  answers: FetchFormResponse['answers'] | undefined;
};

const gdadSkillsMapping = gdadSkillsMappingJson as GdadSkillsMapping;
const gdadSkillNameById: Record<string, string> = Object.fromEntries(
  gdadSkillsMapping.skills.map((skill) => [skill.id, skill.name]),
);

const getLevelFromAverage = (average: number): string => {
  if (average >= 3) return 'Accomplished B';
  if (average >= 2.5) return 'Accomplished A';
  if (average >= 2) return 'Proficient B';
  if (average >= 1.5) return 'Proficient A';
  return 'Developing';
};

const levelDescriptionByName: Record<string, string> = {
  Developing:
    'Works towards most of the required skill levels for the relevant skills in the Capability Framework',
  'Proficient A':
    'Works at most of the required skill levels for the relevant skills in the Capability Framework',
  'Proficient B':
    'Works at all the required skills levels for the relevant skills on the Capability Framework',
  'Accomplished A':
    'Works above most of the required skill levels for the relevant skills on the Capability Framework',
  'Accomplished B':
    'Works above all the required skills levels for the relevant skills on the Capability Framework',
};

const toDisplayAverage = (average: number): string =>
  (Math.floor((average + 1e-9) * 10) / 10).toFixed(1);

function GdadProficiencyRating({ answers }: Props) {
  const role = String(answers?.role || '').trim();
  const roleLevel = String(answers?.['role-level'] || '').trim();
  const mapKey = `${role}||${roleLevel}`;
  const mappedSkills = gdadSkillsMapping.role_level_skills_map[mapKey] || [];

  const skillRows: SkillScoreRow[] = mappedSkills
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((mappedSkill) => {
      const score = getGdadSkillScore(answers?.[`skill-level-${mappedSkill.skill_id}`]);
      return {
        skillName: gdadSkillNameById[mappedSkill.skill_id] || mappedSkill.skill_id,
        score,
      };
    });

  if (skillRows.length === 0) {
    return null;
  }

  const nonZeroRows = skillRows.filter((row) => row.score > 0);
  const nonZeroTotal = nonZeroRows.reduce((total, row) => total + row.score, 0);
  const rawAverage = nonZeroRows.length > 0 ? nonZeroTotal / nonZeroRows.length : 0;
  const displayAverage = toDisplayAverage(rawAverage);
  const proficiencyLevel = getLevelFromAverage(rawAverage);
  const proficiencyLevelDescription = levelDescriptionByName[proficiencyLevel] || '';
  const proficiencyBands = [
    { range: '0 - 1.4', label: 'Developing' },
    { range: '1.5 - 1.9', label: 'Proficient A' },
    { range: '2.0 - 2.4', label: 'Proficient B' },
    { range: '2.5 - 2.9', label: 'Accomplished A' },
    { range: '3', label: 'Accomplished B' },
  ];
  const averageCalculation =
    nonZeroRows.length > 0
      ? `(${nonZeroRows.map((row) => row.score).join(' + ')}) / ${nonZeroRows.length} = ${displayAverage}`
      : 'No non-zero scores selected, so the average is 0.0';

  return (
    <>
      <h2 className="govuk-heading-m">Skills Evaluation Summary</h2>

      <table className="govuk-table">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Skill
            </th>
            <th scope="col" className="govuk-table__header">
              Score
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {skillRows.map((row) => (
            <tr className="govuk-table__row" key={row.skillName}>
              <td className="govuk-table__cell">{row.skillName}</td>
              <td className="govuk-table__cell">{row.score}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="govuk-heading-m govuk-!-margin-bottom-0">Proficiency Rating</h2>
      <p className="govuk-hint govuk-!-margin-bottom-0">Your proficiency rating is shown below</p>

      <details className="govuk-details govuk-!-margin-top-0">
        <summary className="govuk-details__summary">
          <span className="govuk-details__summary-text">Show calculation</span>
        </summary>
        <div className="govuk-details__text">
          <p className="govuk-body govuk-!-margin-bottom-0">
            <strong>Calculation:</strong> {averageCalculation}
          </p>
          <ul className="govuk-list govuk-!-margin-top-3 govuk-!-margin-bottom-0">
            {proficiencyBands.map((band) => (
              <li key={band.range}>
                {band.label === proficiencyLevel ? (
                  <strong>
                    {band.range} = {band.label}
                  </strong>
                ) : (
                  <>
                    {band.range} = {band.label}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </details>

      <p className="govuk-body govuk-!-margin-bottom-2">
        <strong>Your Proficiency Score:</strong> {displayAverage}
      </p>
      <p className="govuk-body">
        <strong>Proficiency Level:</strong> {proficiencyLevel} - {proficiencyLevelDescription}
      </p>
      <br />
    </>
  );
}

export default GdadProficiencyRating;

